import { calendarDteAtCursor, toET } from "@/lib/market/hours";
import {
  EXPIRY_ANCHOR_MS_PARAM,
  layerExpiryDtes,
  resolveLayerExpiryForDte,
} from "@/lib/options/leg-premium";
import type { StrategyLayer } from "@/types/strategy";

const EMPTY_HOLIDAYS = new Set<string>();
const layerExpiryCache = new WeakMap<
  StrategyLayer,
  { key: string; expiryDtes: number[] }
>();
const scopeIndexCache = new WeakMap<
  StrategyLayer[],
  {
    key: string;
    all: Map<number, StrategyLayer[]>;
    enabled: Map<number, StrategyLayer[]>;
  }
>();

function referenceDateKey(reference: Date): string {
  return toET(reference).dateStr;
}

function holidayKey(holidays: Set<string>): string {
  return [...holidays].sort().join(",");
}

function layerExpiryCacheKey(
  layer: StrategyLayer,
  fallbackDte: number,
  holidays: Set<string>,
  reference: Date,
  selectorDtes: number[],
): string {
  return [
    fallbackDte,
    referenceDateKey(reference),
    holidayKey(holidays),
    layer.params[EXPIRY_ANCHOR_MS_PARAM] ?? "",
    selectorDtes.join(","),
  ].join("|");
}

function scopeFingerprint(
  layers: StrategyLayer[],
  fallbackDte: number,
  holidays: Set<string>,
  reference: Date,
): string {
  return [
    fallbackDte,
    referenceDateKey(reference),
    holidayKey(holidays),
    ...layers.map((layer) =>
      [
        layer.id,
        layer.enabled ? 1 : 0,
        layer.params[EXPIRY_ANCHOR_MS_PARAM] ?? "",
        layerExpiryDtes(layer, fallbackDte, true).join(","),
      ].join(":"),
    ),
  ].join("|");
}

export function layerSimulationExpiryDtes(
  layer: StrategyLayer,
  fallbackDte: number,
  holidays: Set<string> = EMPTY_HOLIDAYS,
  reference: Date = new Date(),
): number[] {
  // Date membership belongs to the stored structure, including hidden legs.
  const selectorDtes = layerExpiryDtes(layer, fallbackDte, true);
  const key = layerExpiryCacheKey(
    layer,
    fallbackDte,
    holidays,
    reference,
    selectorDtes,
  );
  const cached = layerExpiryCache.get(layer);
  if (cached?.key === key) return [...cached.expiryDtes];

  const referenceDay = referenceDateKey(reference);
  const expiryDtes = [
    ...new Set(
      selectorDtes.flatMap((expiryDte) => {
        const expiry = resolveLayerExpiryForDte(
          layer,
          expiryDte,
          holidays,
          reference,
        );
        // Calendar DTE clamps past dates to zero for display. Exclude them
        // before indexing so they cannot join today's strategies or slot limit.
        if (expiry.dateStr < referenceDay) return [];
        return [calendarDteAtCursor(reference.getTime(), expiry.closeMs)];
      }),
    ),
  ];
  layerExpiryCache.set(layer, { key, expiryDtes });
  return [...expiryDtes];
}

function simulationExpiryIndex(
  layers: StrategyLayer[],
  fallbackDte: number,
  holidays: Set<string>,
  reference: Date,
) {
  const key = scopeFingerprint(layers, fallbackDte, holidays, reference);
  const cached = scopeIndexCache.get(layers);
  if (cached?.key === key) return cached;

  const all = new Map<number, StrategyLayer[]>();
  const enabled = new Map<number, StrategyLayer[]>();
  for (const layer of layers) {
    for (const expiryDte of layerSimulationExpiryDtes(
      layer,
      fallbackDte,
      holidays,
      reference,
    )) {
      const allLayers = all.get(expiryDte) ?? [];
      allLayers.push(layer);
      all.set(expiryDte, allLayers);
      if (layer.enabled) {
        const enabledLayers = enabled.get(expiryDte) ?? [];
        enabledLayers.push(layer);
        enabled.set(expiryDte, enabledLayers);
      }
    }
  }
  const next = { key, all, enabled };
  scopeIndexCache.set(layers, next);
  return next;
}

export function layerIncludesExpiryDte(
  layer: StrategyLayer,
  selectedExpiryDte: number | null,
  fallbackDte: number,
  holidays: Set<string> = EMPTY_HOLIDAYS,
): boolean {
  if (selectedExpiryDte == null) return true;
  const target = Math.round(selectedExpiryDte);
  return layerSimulationExpiryDtes(layer, fallbackDte, holidays).some(
    (expiryDte) => Math.round(expiryDte) === target,
  );
}

export function simulationScopeLayers(
  layers: StrategyLayer[],
  selectedLayerTab: string | null,
  selectedExpiryDte: number | null,
  fallbackDte: number,
  enabledOnly = true,
  holidays: Set<string> = EMPTY_HOLIDAYS,
): StrategyLayer[] {
  if (selectedExpiryDte == null) {
    const tabScoped = selectedLayerTab
      ? layers.filter((layer) => layer.id === selectedLayerTab)
      : enabledOnly
        ? layers.filter((layer) => layer.enabled)
        : layers;
    return tabScoped;
  }

  if (selectedLayerTab) {
    const selectedLayer = layers.find((layer) => layer.id === selectedLayerTab);
    if (!selectedLayer || (enabledOnly && !selectedLayer.enabled)) return [];
    return layerIncludesExpiryDte(
      selectedLayer,
      selectedExpiryDte,
      fallbackDte,
      holidays,
    )
      ? [selectedLayer]
      : [];
  }

  const target = Math.round(selectedExpiryDte);
  const index = simulationExpiryIndex(
    layers,
    fallbackDte,
    holidays,
    new Date(),
  );
  return [...((enabledOnly ? index.enabled : index.all).get(target) ?? [])];
}
